/**
 * Worker Skills bulk-edit — classify every worker as TRAINEE / UNSKILLED /
 * SEMI_SKILLED / SKILLED in one screen.
 *
 * Route: /hr/worker-skills
 *
 * Why this exists: the Excel bulk-import only carried name + biometric ID,
 * leaving all 159 imported workers with default TRAINEE. Without the right
 * skill class, the Skill Mismatch system can't tell SKILLED-on-TRAINEE-slot
 * (cost waste) from a properly-allocated worker.
 *
 * UX: per-row dropdown for one-off edits + a "Set All Filtered To" bulk bar
 * so HR can sweep through the list by department and apply the right class
 * en masse.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons'

import {
  SKILL_CLASS_COLOR, SKILL_CLASSES, workerSkillsApi,
  type SkillClass, type WorkerSkillRow,
} from '@/features/hr/skillMismatchApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography

export default function WorkerSkillsPage() {
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [deptId, setDeptId] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [classFilter, setClassFilter] = useState<SkillClass | undefined>()
  // Per-row pending changes — applied on Save. Keeps the table snappy
  // and avoids one PATCH per keystroke.
  const [pendingChanges, setPendingChanges] = useState<Record<string, SkillClass>>({})
  // Bulk-apply selection
  const [bulkClass, setBulkClass] = useState<SkillClass | undefined>()
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)

  const { data: depts } = useQuery({
    queryKey: ['departments-all', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200, plant_id: plantId }),
  })
  const deptOptions = (depts?.items ?? []).map((d) => ({ label: d.name, value: d.id }))

  const { data: rows = [], isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['worker-skills', plantId, deptId, classFilter, search],
    queryFn: () => workerSkillsApi.list({
      plant_id: plantId,
      department_id: deptId,
      skill_class: classFilter,
      search: search || undefined,
      only_active: true,
      limit: 1000,
    }),
    enabled: !!plantId,
  })

  const saveMut = useMutation({
    mutationFn: () => workerSkillsApi.bulkSet(
      Object.entries(pendingChanges).map(([employee_id, worker_skill_class]) => ({
        employee_id, worker_skill_class,
      })),
    ),
    onSuccess: (r) => {
      message.success(
        `Updated ${r.updated} worker(s). ` +
        `${r.skipped_invalid_class + r.skipped_employee_not_found > 0
          ? `Skipped ${r.skipped_invalid_class + r.skipped_employee_not_found}.`
          : ''}`,
      )
      setPendingChanges({})
      qc.invalidateQueries({ queryKey: ['worker-skills'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  // Summary counts for the cards at the top
  const counts = useMemo(() => {
    const out: Record<SkillClass | 'total', number> = {
      TRAINEE: 0, UNSKILLED: 0, SEMI_SKILLED: 0, SKILLED: 0, total: rows.length,
    }
    for (const r of rows) {
      const current = pendingChanges[r.id] ?? r.worker_skill_class
      if (current in out) out[current] += 1
    }
    return out
  }, [rows, pendingChanges])

  const pendingCount = Object.keys(pendingChanges).length

  const applyBulk = () => {
    if (!bulkClass) return
    const next = { ...pendingChanges }
    for (const r of rows) {
      const current = pendingChanges[r.id] ?? r.worker_skill_class
      if (current !== bulkClass) next[r.id] = bulkClass
    }
    setPendingChanges(next)
    setBulkConfirmOpen(false)
    setBulkClass(undefined)
    message.info(`Queued ${rows.length} workers → ${bulkClass}. Click Save to apply.`)
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Worker Skills</Title>
      <Text type="secondary">
        Classify every worker as TRAINEE / UNSKILLED / SEMI_SKILLED / SKILLED.
        Drives the Skill Mismatch system — pick wrong here and the allocator can't tell cost waste from quality risk.
      </Text>

      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small"><Statistic title="Total" value={counts.total} /></Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card size="small">
            <Statistic title="Skilled" value={counts.SKILLED}
              valueStyle={{ color: '#389e0d' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card size="small">
            <Statistic title="Semi-Skilled" value={counts.SEMI_SKILLED}
              valueStyle={{ color: '#08979c' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card size="small">
            <Statistic title="Unskilled" value={counts.UNSKILLED}
              valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card size="small">
            <Statistic title="Trainee (incl. unset)" value={counts.TRAINEE}
              valueStyle={{ color: '#8c8c8c' }} />
          </Card>
        </Col>
      </Row>

      {/* Filters + bulk-apply bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select placeholder="Filter by department" style={{ width: 240 }}
            allowClear showSearch optionFilterProp="label"
            value={deptId} onChange={setDeptId}
            options={deptOptions} />
          <Select placeholder="Filter by skill class" style={{ width: 200 }}
            allowClear value={classFilter} onChange={setClassFilter}
            options={SKILL_CLASSES.map((c) => ({ label: c, value: c }))} />
          <Input.Search placeholder="Name / code / biometric"
            allowClear value={search}
            onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isRefetching}>
            Refresh
          </Button>
          <span style={{ borderLeft: '1px solid #d9d9d9', height: 24, margin: '0 8px' }} />
          <Text>Bulk apply to filtered ({rows.length}):</Text>
          <Select placeholder="Set all to..." style={{ width: 160 }}
            value={bulkClass} onChange={setBulkClass}
            options={SKILL_CLASSES.map((c) => ({ label: c, value: c }))} />
          <Button
            type="dashed" icon={<ThunderboltOutlined />}
            disabled={!bulkClass || rows.length === 0}
            onClick={() => setBulkConfirmOpen(true)}
          >
            Queue
          </Button>
        </Space>
      </Card>

      {pendingCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            <Space>
              <Text strong>{pendingCount} pending change(s)</Text>
              <Button size="small" onClick={() => setPendingChanges({})}>Discard</Button>
              <Button type="primary" size="small"
                onClick={() => saveMut.mutate()}
                loading={saveMut.isPending}>
                Save {pendingCount} change(s)
              </Button>
            </Space>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Table<WorkerSkillRow>
          dataSource={rows}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          size="small"
          columns={[
            {
              title: 'Employee', key: 'emp', width: 260,
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{r.emp_name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.emp_code ?? 'no code'} · BIO {r.biometric_id ?? '—'}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Department', dataIndex: 'department_name', width: 160,
              render: (v) => v ?? '—',
            },
            {
              title: 'Designation', dataIndex: 'designation', width: 160,
              render: (v) => v ?? <Text type="secondary">—</Text>,
            },
            {
              title: 'Current Class', key: 'current_class', width: 140,
              render: (_, r) => (
                <Tag color={SKILL_CLASS_COLOR[r.worker_skill_class]}>{r.worker_skill_class}</Tag>
              ),
            },
            {
              title: 'New Class', key: 'new_class', width: 160,
              render: (_, r) => {
                const pending = pendingChanges[r.id]
                return (
                  <Select
                    value={pending ?? r.worker_skill_class}
                    style={{ width: '100%' }}
                    onChange={(v) => {
                      const next = { ...pendingChanges }
                      if (v === r.worker_skill_class) {
                        delete next[r.id]
                      } else {
                        next[r.id] = v
                      }
                      setPendingChanges(next)
                    }}
                    options={SKILL_CLASSES.map((c) => ({ label: c, value: c }))}
                    status={pending ? 'warning' : undefined}
                  />
                )
              },
            },
            {
              title: 'Status', key: 'status', width: 100,
              render: (_, r) => {
                if (pendingChanges[r.id]) return <Badge status="processing" text="Queued" />
                return <Badge status="success" text="Saved" />
              },
            },
          ]}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        open={bulkConfirmOpen}
        title={`Apply "${bulkClass}" to all ${rows.length} filtered workers?`}
        onCancel={() => setBulkConfirmOpen(false)}
        onOk={applyBulk}
        okText="Queue"
      >
        <Text>
          This will set <Tag color={bulkClass ? SKILL_CLASS_COLOR[bulkClass] : 'default'}>{bulkClass}</Tag>
          on every worker currently visible in the table.
          Changes are queued — review them in the table and click <b>Save</b> to commit.
        </Text>
      </Modal>
    </div>
  )
}
